/**
 * The wallet option on the screen before a start, as the selector renders it
 * (Warren, 2026-10-25): "Wallet always shown on the screen before a start,
 * with its balance and either 'Use wallet' or the exact reason it can't be
 * used yet."
 *
 * The decision is the start gate's: GET /chargers/:id/start-preview returns
 * the gate's own wallet option, and the wallet is selectable exactly when
 * that option is usable (walletChoice, @evi/shared). Nothing here decides;
 * it only maps the option to copy and to the top-up link. An older Api
 * without the option falls back to the fields it always sent.
 *
 * Pure (no React), so node tests run it directly.
 */
import { formatWalletFxRate, walletChoice } from '@evi/shared';
import type { StartPreviewResponse, WalletConversionView, WalletUnavailableReason } from '@/lib/api';

export interface WalletOptionView {
  /** The start would take wallet mode if the driver picks the wallet. */
  selectable: boolean;
  reason: WalletUnavailableReason | null;
  /** The wallet shown: its cash balance, in its own currency. */
  balanceCents: number;
  currency: string;
  /** The price's currency. */
  sessionCurrency: string;
  /** Operator minimum, in the price's currency. */
  requiredCents: number;
  /** What to add, in `topUpCurrency` (the price's currency, or USD for a
   *  converted USD wallet at a CAD charger). */
  shortfallCents: number;
  topUpCurrency: string;
  /** The wallet page in the currency to top up, or null when no top-up can
   *  help (fleet, guest, outstanding balance, no rate). */
  topUpPath: string | null;
  /** A USD wallet at a CAD charger: its CAD value at the Bank of Canada rate
   *  the start would pin (2026-10-25T02:52:41Z ruling). Null otherwise. */
  conversion: WalletConversionView | null;
}

/** The wallet page, opened on one currency's top-up. */
export function walletTopUpPath(currency: string, amountCents?: number): string {
  const q = new URLSearchParams({ currency });
  if (amountCents && amountCents > 0) q.set('amount', String(amountCents));
  return `/wallet?${q.toString()}`;
}

export function walletOptionView(preview: StartPreviewResponse): WalletOptionView {
  const w = preview.wallet;
  const priceCurrency = (preview.tariff?.currency ?? w.sessionCurrency ?? 'USD').toUpperCase();
  if (typeof w.usable === 'boolean') {
    const choice = walletChoice({
      usable: w.usable,
      reason: w.reason ?? null,
      topUpCurrency: (w.topUpCurrency ?? null) as 'USD' | 'CAD' | null,
      shortfallCents: w.shortfallCents ?? 0,
    });
    const sessionCurrency = w.sessionCurrency ?? priceCurrency;
    return {
      selectable: choice.selectable,
      reason: choice.reason as WalletUnavailableReason | null,
      balanceCents: w.cashCents ?? w.balanceCents,
      currency: w.currency ?? priceCurrency,
      sessionCurrency,
      requiredCents: w.requiredCents ?? w.minimumBalanceCents,
      shortfallCents: w.shortfallCents ?? 0,
      topUpCurrency: w.topUpCurrency ?? sessionCurrency,
      topUpPath: choice.topUp ? walletTopUpPath(choice.topUp.currency, choice.topUp.amountCents) : null,
      conversion: w.conversion ?? null,
    };
  }
  // An older Api: USD wallets only, the rules the selector always applied.
  const currency = w.currency ?? 'USD';
  const guest = preview.accessMode === 'guest' && preview.mode !== 'wallet';
  const cad = priceCurrency === 'CAD';
  const selectable = w.sufficient && w.available !== false && !cad && !guest;
  const shortfall = Math.max(0, w.minimumBalanceCents - w.balanceCents);
  const reason: WalletUnavailableReason | null = selectable ? null
    : cad ? 'CURRENCY_MISMATCH'
    : guest ? 'CROSS_TENANT'
    : w.available === false ? 'FLEET_SPONSORED'
    : 'BELOW_MINIMUM';
  return {
    selectable,
    reason,
    balanceCents: w.balanceCents,
    currency,
    sessionCurrency: priceCurrency,
    requiredCents: w.minimumBalanceCents,
    shortfallCents: selectable ? 0 : shortfall,
    topUpCurrency: currency,
    topUpPath: reason === 'BELOW_MINIMUM' ? walletTopUpPath(currency, shortfall) : null,
    conversion: null,
  };
}

/** The balance line: the wallet in its own currency and, for a USD wallet at
 *  a CAD charger, what it is worth here at today's rate
 *  ("US$29.12 (about CA$40.76 at today's rate)"), the number the start
 *  checks and the debit converts at. */
export function walletBalanceCopy(view: WalletOptionView, money: (cents: number, currency: string) => string): {
  key: string; values: Record<string, string>;
} {
  const amount = money(view.balanceCents, view.currency);
  if (!view.conversion) return { key: 'selector.walletBalance', values: { amount } };
  return { key: 'selector.walletBalanceConverted',
    values: { amount, converted: money(view.conversion.valueCents, view.conversion.sessionCurrency) } };
}

/** The rate line under a converted wallet, or null: the Bank of Canada rate
 *  and its date, as the receipt will print them. */
export function walletRateCopy(view: WalletOptionView): { key: string; values: Record<string, string> } | null {
  if (!view.conversion) return null;
  return { key: 'selector.walletRate', values: { rate: formatWalletFxRate(view.conversion.rate), date: view.conversion.rateDate } };
}

/** The i18n key and values of the line under the balance: "Use wallet", or
 *  the reason with the amounts it needs (formatted by the caller). */
export function walletStateCopy(view: WalletOptionView, money: (cents: number, currency: string) => string): {
  key: string; values: Record<string, string>;
} {
  if (view.selectable) return { key: 'selector.walletUse', values: {} };
  const shortfall = money(view.shortfallCents, view.topUpCurrency);
  const minimum = money(view.requiredCents, view.sessionCurrency);
  switch (view.reason) {
    case 'CURRENCY_MISMATCH':
      return { key: 'selector.walletReason.currencyMismatch',
        values: { walletCurrency: view.currency, sessionCurrency: view.sessionCurrency, shortfall } };
    case 'CROSS_TENANT':
      return { key: 'selector.walletNotUsableHere', values: {} };
    case 'FLEET_SPONSORED':
      return { key: 'selector.walletReason.fleetSponsored', values: {} };
    case 'OUTSTANDING_BALANCE':
      return { key: 'selector.walletReason.outstanding', values: {} };
    case 'UNSUPPORTED_CURRENCY':
      return { key: 'selector.walletReason.unsupportedCurrency', values: { sessionCurrency: view.sessionCurrency } };
    case 'FX_RATE_UNAVAILABLE':
      return { key: 'selector.walletReason.fxRateUnavailable', values: {} };
    case 'BELOW_MINIMUM':
    default:
      return { key: 'selector.walletReason.belowMinimum', values: { shortfall, minimum } };
  }
}
